import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Badge,
  LinearProgress,
  Tooltip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Camera,
  DirectionsCar,
  LocalShipping,
  Star,
  Person,
  AccessTime,
  LocationOn,
  Refresh,
  Fullscreen,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// 樣式化組件
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const CameraContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '300px',
  backgroundColor: '#000',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CameraImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const PlateOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  border: '3px solid #4caf50',
  borderRadius: '4px',
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  pointerEvents: 'none',
}));

const StatusIndicator = styled(Box)<{ status: string }>(({ theme, status }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: 
    status === 'online' ? '#4caf50' :
    status === 'offline' ? '#f44336' :
    status === 'processing' ? '#ff9800' : '#9e9e9e',
  animation: status === 'processing' ? 'pulse 1.5s infinite' : 'none',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 },
  },
}));

// 類型定義
interface PlateRecognitionResult {
  recognitionId: string;
  success: boolean;
  plateNumber: string;
  plateFormat: string;
  vehicleType: 'vip' | 'regular' | 'temporary';
  vehicleInfo: any;
  confidence: number;
  processingTime: number;
  timestamp: string;
  cameraInfo: any;
  imagePath: string;
  plateRegion?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  error?: string;
}

interface CameraData {
  cameraId: string;
  name: string;
  location: {
    zone: string;
    floor: string;
    laneId: string;
    description: string;
  };
  status: 'online' | 'offline' | 'error' | 'processing';
  lastCapture?: string;
  captureCount: number;
  errorCount: number;
}

interface PlateRecognitionDisplayProps {
  cameraId?: string;
  showAllCameras?: boolean;
  onRecognitionResult?: (result: PlateRecognitionResult) => void;
}

const PlateRecognitionDisplay: React.FC<PlateRecognitionDisplayProps> = ({
  cameraId,
  showAllCameras = false,
  onRecognitionResult
}) => {
  // 狀態管理
  const [cameras, setCameras] = useState<CameraData[]>([]);
  const [recognitionResults, setRecognitionResults] = useState<PlateRecognitionResult[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 初始化
  useEffect(() => {
    initializeComponent();
    return () => {
      cleanup();
    };
  }, []);

  const initializeComponent = async () => {
    try {
      setLoading(true);
      await loadCameras();
      setupWebSocket();
      startPeriodicUpdates();
    } catch (err) {
      setError('初始化車牌辨識顯示組件失敗');
      console.error('初始化失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const cleanup = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  // 載入攝像頭列表
  const loadCameras = async () => {
    try {
      const response = await fetch('/api/plate-recognition/cameras');
      if (!response.ok) throw new Error('載入攝像頭失敗');
      
      const data = await response.json();
      setCameras(data.cameras || []);
      
      if (cameraId && !selectedCamera) {
        setSelectedCamera(cameraId);
      } else if (data.cameras.length > 0 && !selectedCamera) {
        setSelectedCamera(data.cameras[0].cameraId);
      }
    } catch (err) {
      console.error('載入攝像頭失敗:', err);
      // 使用模擬資料
      const mockCameras: CameraData[] = [
        {
          cameraId: 'RTSP_CAM_001',
          name: '入口車牌辨識攝像頭',
          location: {
            zone: 'entrance',
            floor: 'ground',
            laneId: 'ENTRY_CAR_01',
            description: '停車場主入口'
          },
          status: 'online',
          captureCount: 1234,
          errorCount: 2
        },
        {
          cameraId: 'RTSP_CAM_002',
          name: '出口車牌辨識攝像頭',
          location: {
            zone: 'exit',
            floor: 'ground',
            laneId: 'EXIT_CAR_01',
            description: '停車場主出口'
          },
          status: 'online',
          captureCount: 987,
          errorCount: 0
        }
      ];
      setCameras(mockCameras);
      if (!selectedCamera) {
        setSelectedCamera(mockCameras[0].cameraId);
      }
    }
  };

  // 設置 WebSocket 連接
  const setupWebSocket = () => {
    try {
      const socketBase = (process.env.REACT_APP_SOCKET_URL || window.location.origin).replace(/^http/, 'ws');
      const ws = new WebSocket(`${socketBase}/ws/plate-recognition`);
      
      ws.onopen = () => {
        console.log('車牌辨識 WebSocket 連接已建立');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (err) {
          console.error('解析 WebSocket 消息失敗:', err);
        }
      };
      
      ws.onclose = () => {
        console.log('車牌辨識 WebSocket 連接已關閉');
        // 嘗試重新連接
        setTimeout(setupWebSocket, 5000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket 錯誤:', error);
      };
      
      wsRef.current = ws;
    } catch (err) {
      console.error('設置 WebSocket 失敗:', err);
    }
  };

  // 處理 WebSocket 消息
  const handleWebSocketMessage = (data: any) => {
    switch (data.type) {
      case 'plateRecognized':
        handleNewRecognition(data.result);
        break;
      case 'cameraStatus':
        updateCameraStatus(data.cameraId, data.status);
        break;
      case 'imageCapture':
        updateCameraCapture(data.cameraId, data.imagePath);
        break;
      default:
        console.log('未知的 WebSocket 消息類型:', data.type);
    }
  };

  // 處理新的辨識結果
  const handleNewRecognition = (result: PlateRecognitionResult) => {
    setRecognitionResults(prev => [result, ...prev.slice(0, 49)]); // 保留最近50條記錄
    
    // 更新攝像頭狀態
    setCameras(prev => prev.map(camera => 
      camera.cameraId === result.cameraInfo?.cameraId
        ? { ...camera, status: 'online', captureCount: camera.captureCount + 1 }
        : camera
    ));
    
    // 回調通知
    if (onRecognitionResult) {
      onRecognitionResult(result);
    }
  };

  // 更新攝像頭狀態
  const updateCameraStatus = (cameraId: string, status: string) => {
    setCameras(prev => prev.map(camera => 
      camera.cameraId === cameraId
        ? { ...camera, status: status as any }
        : camera
    ));
  };

  // 更新攝像頭擷取
  const updateCameraCapture = (cameraId: string, imagePath: string) => {
    setCameras(prev => prev.map(camera => 
      camera.cameraId === cameraId
        ? { ...camera, lastCapture: imagePath }
        : camera
    ));
  };

  // 定期更新
  const startPeriodicUpdates = () => {
    intervalRef.current = setInterval(() => {
      loadCameras();
    }, 30000); // 每30秒更新一次
  };

  // 手動觸發辨識
  const triggerManualRecognition = async (cameraId: string) => {
    try {
      const response = await fetch(`/api/plate-recognition/cameras/${cameraId}/manual-capture`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('手動辨識失敗');
      
      const result = await response.json();
      console.log('手動辨識結果:', result);
    } catch (err) {
      console.error('手動辨識失敗:', err);
    }
  };

  // 獲取車輛類型圖標
  const getVehicleTypeIcon = (vehicleType: string) => {
    switch (vehicleType) {
      case 'vip':
        return <Star sx={{ color: '#ffd700' }} />;
      case 'regular':
        return <DirectionsCar sx={{ color: '#2196f3' }} />;
      case 'temporary':
        return <AccessTime sx={{ color: '#ff9800' }} />;
      default:
        return <DirectionsCar />;
    }
  };

  // 獲取車輛類型標籤
  const getVehicleTypeLabel = (vehicleType: string) => {
    switch (vehicleType) {
      case 'vip':
        return 'VIP會員';
      case 'regular':
        return '一般車輛';
      case 'temporary':
        return '臨時停車';
      default:
        return '未知';
    }
  };

  // 獲取車輛類型顏色
  const getVehicleTypeColor = (vehicleType: string) => {
    switch (vehicleType) {
      case 'vip':
        return 'warning';
      case 'regular':
        return 'primary';
      case 'temporary':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // 渲染攝像頭卡片
  const renderCameraCard = (camera: CameraData) => {
    const latestResult = recognitionResults.find(r => 
      r.cameraInfo?.cameraId === camera.cameraId
    );
    const recentRecords = recognitionResults
      .filter(r => r.cameraInfo?.cameraId === camera.cameraId)
      .slice(0, 10);

    return (
      <Box 
        key={camera.cameraId}
        width={showAllCameras ? "48%" : "100%"}
        minWidth="400px"
        mb={3}
      >
        <StyledCard>
          <CardContent>
            {/* 攝像頭標題 */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Camera />
                <Typography variant="h6">{camera.name}</Typography>
                <StatusIndicator status={camera.status} />
              </Box>
              <Box display="flex" gap={1}>
                <Tooltip title="手動辨識">
                  <IconButton 
                    size="small" 
                    onClick={() => triggerManualRecognition(camera.cameraId)}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="全螢幕">
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      setSelectedCamera(camera.cameraId);
                      setIsFullscreen(true);
                    }}
                  >
                    <Fullscreen />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* 攝像頭影像 */}
            <CameraContainer>
              {camera.lastCapture ? (
                <>
                  <CameraImage 
                    src={`/api/images/${camera.lastCapture}`} 
                    alt={camera.name}
                    onError={(e) => {
                      // 如果影像載入失敗，顯示預設影像
                      (e.target as HTMLImageElement).src = '/images/no-camera.png';
                    }}
                  />
                  {latestResult?.plateRegion && (
                    <PlateOverlay
                      sx={{
                        left: `${(latestResult.plateRegion.x / 800) * 100}%`,
                        top: `${(latestResult.plateRegion.y / 600) * 100}%`,
                        width: `${(latestResult.plateRegion.width / 800) * 100}%`,
                        height: `${(latestResult.plateRegion.height / 600) * 100}%`,
                      }}
                    />
                  )}
                </>
              ) : (
                <Box textAlign="center" color="white">
                  <Camera sx={{ fontSize: 48, mb: 1 }} />
                  <Typography>等待影像...</Typography>
                </Box>
              )}
            </CameraContainer>

            {/* 攝像頭資訊 */}
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                位置: {camera.location.description}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                車道: {camera.location.laneId}
              </Typography>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">
                  擷取次數: {camera.captureCount}
                </Typography>
                <Typography variant="body2" color={camera.errorCount > 0 ? 'error' : 'textSecondary'}>
                  錯誤次數: {camera.errorCount}
                </Typography>
              </Box>
            </Box>

            {/* 最新辨識結果 */}
            {latestResult && (
              <Box mt={2}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  最新辨識結果
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Chip
                    icon={getVehicleTypeIcon(latestResult.vehicleType)}
                    label={latestResult.plateNumber}
                    color={getVehicleTypeColor(latestResult.vehicleType) as any}
                    variant="outlined"
                  />
                  <Chip
                    label={getVehicleTypeLabel(latestResult.vehicleType)}
                    size="small"
                    color={getVehicleTypeColor(latestResult.vehicleType) as any}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    信心度: {(latestResult.confidence * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {new Date(latestResult.timestamp).toLocaleTimeString()}
                  </Typography>
                </Box>
                {latestResult.confidence < 0.8 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    辨識信心度較低，建議人工確認
                  </Alert>
                )}
              </Box>
            )}

            {/* 最近十筆辨識記錄 */}
            {recentRecords.length > 0 && (
              <Box mt={2}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  最近辨識（10筆）
                </Typography>
                <List dense>
                  {recentRecords.map((result) => (
                    <ListItem key={result.recognitionId} disableGutters>
                      <ListItemIcon>
                        {result.success ? (
                          <CheckCircle color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={result.plateNumber || '辨識失敗'}
                              size="small"
                              color={result.success ? (getVehicleTypeColor(result.vehicleType) as any) : 'default'}
                              variant={result.success ? 'outlined' : 'filled'}
                            />
                            {result.success && (
                              <Chip
                                label={`${(result.confidence * 100).toFixed(0)}%`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption" color="textSecondary">
                              {getVehicleTypeLabel(result.vehicleType)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(result.timestamp).toLocaleTimeString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Box>
    );
  };

  // 渲染辨識歷史
  const renderRecognitionHistory = () => (
    <Dialog 
      open={showHistory} 
      onClose={() => setShowHistory(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">辨識歷史記錄</Typography>
          <IconButton onClick={() => setShowHistory(false)}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <List>
          {recognitionResults.map((result, index) => (
            <ListItem key={result.recognitionId} divider>
              <ListItemIcon>
                {result.success ? (
                  <CheckCircle color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1">
                      {result.plateNumber || '辨識失敗'}
                    </Typography>
                    {result.success && (
                      <Chip
                        label={getVehicleTypeLabel(result.vehicleType)}
                        size="small"
                        color={getVehicleTypeColor(result.vehicleType) as any}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      攝像頭: {result.cameraInfo?.name || '未知'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      時間: {new Date(result.timestamp).toLocaleString()}
                    </Typography>
                    {result.success && (
                      <Typography variant="body2" color="textSecondary">
                        信心度: {(result.confidence * 100).toFixed(1)}% | 
                        處理時間: {result.processingTime}ms
                      </Typography>
                    )}
                    {result.error && (
                      <Typography variant="body2" color="error">
                        錯誤: {result.error}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );

  // 渲染全螢幕對話框
  const renderFullscreenDialog = () => {
    const camera = cameras.find(c => c.cameraId === selectedCamera);
    if (!camera) return null;

    return (
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: { width: '90vw', height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{camera.name}</Typography>
            <IconButton onClick={() => setIsFullscreen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: '70vh' }}>
            <CameraContainer sx={{ height: '100%' }}>
              {camera.lastCapture ? (
                <CameraImage 
                  src={`/api/images/${camera.lastCapture}`} 
                  alt={camera.name}
                />
              ) : (
                <Box textAlign="center" color="white">
                  <Camera sx={{ fontSize: 96, mb: 2 }} />
                  <Typography variant="h6">等待影像...</Typography>
                </Box>
              )}
            </CameraContainer>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          正在載入車牌辨識系統...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box p={3}>
      {/* 標題和控制按鈕 */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" gutterBottom>
          車牌辨識監控系統
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => setShowHistory(true)}
          >
            辨識歷史
          </Button>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={loadCameras}
          >
            重新整理
          </Button>
        </Box>
      </Box>

      {/* 統計資訊 */}
      <Box display="flex" flexWrap="wrap" gap={3} mb={3}>
        <Box flex="1" minWidth="250px">
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {cameras.filter(c => c.status === 'online').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                在線攝像頭
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box flex="1" minWidth="250px">
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {recognitionResults.filter(r => r.success).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                成功辨識
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box flex="1" minWidth="250px">
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {recognitionResults.filter(r => r.vehicleType === 'vip').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                VIP 車輛
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box flex="1" minWidth="250px">
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {cameras.reduce((sum, c) => sum + c.captureCount, 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                總擷取次數
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* 攝像頭顯示 */}
      <Box display="flex" flexWrap="wrap" gap={3} justifyContent={showAllCameras ? "space-between" : "center"}>
        {cameras.map(camera => renderCameraCard(camera))}
      </Box>

      {/* 對話框 */}
      {renderRecognitionHistory()}
      {renderFullscreenDialog()}
    </Box>
  );
};

export default PlateRecognitionDisplay;